import { type FC } from 'react';

import {
	NoticeBody,
	NoticeEyebrow,
	NoticeGlyph,
	NoticeRoot,
	NoticeText,
	type TrustedInviteNoticeVariant,
} from './TrustedInviteNotice.styled';

export const TRUSTED_INVITE_EYEBROW = 'Trusted agent invitation';

// Shared shield-check glyph. `currentColor` so it inherits whichever surface
// it sits on (the branded hero, the page, the success-page tag).
export const ShieldCheckGlyph: FC<{ className?: string }> = ({ className }) => (
	<svg
		className={className}
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth='1.9'
		strokeLinecap='round'
		strokeLinejoin='round'
		aria-hidden='true'
		focusable='false'
	>
		<path d='M12 3 4.5 6v5.2c0 4.6 3.1 8.5 7.5 9.8 4.4-1.3 7.5-5.2 7.5-9.8V6L12 3Z' />
		<path d='m9 12.2 2 2 4-4.6' />
	</svg>
);

interface TrustedInviteNoticeProps {
	variant: TrustedInviteNoticeVariant;
	partyName?: string | null;
}

/**
 * Step-1 notice shown only when the application page was opened from the
 * party's trusted link (`trusted=1`). Purely informational — there is no input
 * behind it; the flag travels from the URL to the submit payload in state.
 */
const TrustedInviteNotice: FC<TrustedInviteNoticeProps> = ({ variant, partyName }) => {
	const party = partyName?.trim() || 'Your party';

	return (
		<NoticeRoot
			role='note'
			variant={variant}
		>
			<NoticeGlyph
				className='notice-glyph'
				aria-hidden='true'
			>
				<ShieldCheckGlyph />
			</NoticeGlyph>
			<NoticeBody>
				<NoticeEyebrow className='notice-eyebrow'>{TRUSTED_INVITE_EYEBROW}</NoticeEyebrow>
				<NoticeText className='notice-text'>
					{party} has invited you as a trusted agent. Trusted agents are vetted by the party and are typically placed in priority
					roles. Your application will be marked as trusted for the party to confirm.
				</NoticeText>
			</NoticeBody>
		</NoticeRoot>
	);
};

export default TrustedInviteNotice;
